import React, { useState, useEffect } from 'react';
import { 
  View, 
  Text, 
  StyleSheet, 
} from 'react-native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { 
  PaymentFormInput, 
  PaymentCheckbox, 
  PaymentSecurityNote, 
  PaymentFormFooter 
} from '@/components/residents/payments';
import { usePaymentForm } from '@/features/payments/hooks/usePaymentForm';
import { usePaymentData } from '@/features/payments/hooks/usePaymentData';
import { 
  useAddCreditCardMutation,
} from '@/store/api';
import { detectCardType, formatCardNumber, formatExpDate, isValidCardNumber, isValidCvv, isValidExpDate } from '@/utils/residents/payment/cards';
import { tokenizeCard } from '@/utils/residents/payment/paymentsGateway';

interface Props {
  leaseId: number;
  onSuccess: () => void;
  onBack: () => void;
  showMakeDefault?: boolean;
  showInstructions?: boolean;
}

interface CreditCardFormData {
  name: string;
  cardNumber: string;
  expDate: string;
  cvv: string;
  isDefault: boolean;
}

// Credit card validation types (simplified version)
type CardType = NonNullable<ReturnType<typeof detectCardType>>;

export const CreditCardDetailsForm: React.FC<Props> = ({
  leaseId,
  onSuccess,
  onBack,
  showMakeDefault = false,
  showInstructions = false,
}) => {
  // Get payment data for refreshing after success
  const { refreshPaymentMethods } = usePaymentData();
  
  const {
    formData,
    errors,
    isSubmitting,
    updateField,
    submitForm,
  } = usePaymentForm<CreditCardFormData>({
    initialData: {
      name: '',
      cardNumber: '',
      expDate: '',
      cvv: '',
      isDefault: false,
    },
    onSuccess: () => {
      // Refresh payment methods data after success
      refreshPaymentMethods();
      onSuccess();
    },
    successMessage: 'Credit card added successfully',
  });
  
  const [cardType, setCardType] = useState<CardType | null>(null);
  
  const [addCreditCard, { isLoading: isAddingCard }] = useAddCreditCardMutation();
  
  const isLoading = isAddingCard;

  // Card validation functions now imported from utils

  const validateForm = (data: CreditCardFormData) => {
    const newErrors: any = {};
    
    if (!data.name.trim()) {
      newErrors.name = 'Required';
    }
    
    if (!data.cardNumber) {
      newErrors.cardNumber = 'Required';
    } else if (!isValidCardNumber(data.cardNumber)) {
      newErrors.cardNumber = 'Invalid card number';
    }
    
    if (!data.expDate) {
      newErrors.expDate = 'Required';
    } else if (!isValidExpDate(data.expDate)) {
      newErrors.expDate = 'Invalid expiry date';
    }
    
    if (!data.cvv) {
      newErrors.cvv = 'Required';
    } else if (!isValidCvv(data.cvv, data.cardNumber)) {
      newErrors.cvv = 'Invalid CVV/CVC';
    }
    
    return newErrors;
  };

  // Update card type when card number changes
  useEffect(() => {
    const detectedType = detectCardType(formData.cardNumber);
    setCardType(detectedType);
  }, [formData.cardNumber]);

  const handleInputChange = (field: keyof CreditCardFormData, value: string) => {
    let processedValue = value;
    
    if (field === 'cardNumber') {
      processedValue = formatCardNumber(value);
    } else if (field === 'expDate') {
      processedValue = formatExpDate(value);
    } else if (field === 'cvv') {
      processedValue = value.replace(/\D/g, '').substring(0, 4);
    }
    
    updateField(field, processedValue);
  };

  const handleSubmit = async () => {
    await submitForm(async (data) => {
      const cleanCardNumber = data.cardNumber.replace(/\s/g, '');
      // Convert MM/YYYY -> MMYY (gateway expects 2-digit year)
      const [mm, yyyy] = data.expDate.split('/');
      const expiry = `${mm}${yyyy.slice(-2)}`;
      
      // Tokenize via gateway util
      const token = await tokenizeCard({ account: cleanCardNumber, cvv: data.cvv, expiry });

      // Add credit card - match web implementation exactly
      const payload = {
        name: data.name,
        token,
        expiry,
        isDefault: data.isDefault,
      };
      
      return await addCreditCard(payload).unwrap();
    }, validateForm);
  };

  const getCardIcon = () => {
    if (!cardType) return null;
    
    // Match web app's getCardIcon function exactly
    const iconMap: Record<string, keyof typeof RESIDENTS_SVG_COMPONENTS.cards> = {
      'visa': 'VISA',
      'mastercard': 'MASTERCARD',
      'american-express': 'AMEX',
      'discover': 'DISCOVER',
      'diners': 'DINERS',
      'jcb': 'JCB',
      'maestro': 'MAESTRO',
      'unionpay': 'UNIONPAY',
    };
    
    const iconName = iconMap[cardType.type];
    const IconComponent = iconName ? RESIDENTS_SVG_COMPONENTS.cards[iconName] : null;
    
    return IconComponent ? (
      <IconComponent 
        width={32} 
        height={20}
        color={RESIDENTS_DESKTOP_COLORS.ICON_ACTIVE}
      />
    ) : null;
  };

  return (
    <View style={styles.container}>
      {showInstructions && (
        <Text style={styles.instructions}>
          Please enter your card details here.
        </Text>
      )}

      {/* Card icons display - matching web app */}
      <View style={styles.acceptedCardsContainer}>
        <View style={styles.acceptedCardsRow}>
          <RESIDENTS_SVG_COMPONENTS.cards.VISA width={32} height={20} />
          <RESIDENTS_SVG_COMPONENTS.cards.MASTERCARD width={32} height={20} />
          <RESIDENTS_SVG_COMPONENTS.cards.DISCOVER width={32} height={20} />
          <RESIDENTS_SVG_COMPONENTS.cards.AMEX width={32} height={20} />
        </View>
        <Text style={styles.feeNotice}>
          To cover the cost of accepting credit cards, we will collect a 3% credit card surcharge.
        </Text>
      </View>
      
      <View style={styles.form}>
        <PaymentFormInput
          label="Name"
          value={formData.name}
          onChangeText={(value) => handleInputChange('name', value)}
          placeholder="Name"
          error={errors.name}
          autoCapitalize="words"
        />

        <View style={styles.cardNumberContainer}>
          <PaymentFormInput
            label="Card Number"
            value={formData.cardNumber}
            onChangeText={(value) => handleInputChange('cardNumber', value)}
            placeholder="Card number"
            error={errors.cardNumber}
            keyboardType="numeric"
          />
          {cardType && (
            <View style={styles.cardIcon}>
              {getCardIcon()}
            </View>
          )}
        </View>

        <View style={styles.row}>
          <PaymentFormInput
            label="Exp. MM/YYYY"
            value={formData.expDate}
            onChangeText={(value) => handleInputChange('expDate', value)}
            placeholder="MM/YYYY"
            error={errors.expDate}
            keyboardType="numeric"
            maxLength={7}
            style={styles.halfWidth}
          />

          <PaymentFormInput
            label={cardType?.code.name || 'CVV/CVC'}
            value={formData.cvv}
            onChangeText={(value) => handleInputChange('cvv', value)}
            placeholder={cardType?.code.name || 'CVV/CVC'}
            error={errors.cvv}
            keyboardType="numeric"
            maxLength={cardType?.code.size || 4}
            secureTextEntry={true}
            style={styles.halfWidth}
          />
        </View>

        {showMakeDefault && (
          <PaymentCheckbox
            checked={formData.isDefault}
            onToggle={() => updateField('isDefault', !formData.isDefault)}
            label="Make this my default payment method"
          />
        )}

        <PaymentSecurityNote />
      </View>

      <PaymentFormFooter
        onBack={onBack}
        onSubmit={handleSubmit}
        submitDisabled={!formData.name.trim() || !isValidCardNumber(formData.cardNumber) || !isValidExpDate(formData.expDate) || !isValidCvv(formData.cvv, formData.cardNumber)}
        submitLoading={isSubmitting || isLoading}
      />
    </View>
  );
};


const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  instructions: {
    fontSize: 16,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginBottom: 24,
  },
  form: {
    flex: 1,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  halfWidth: {
    flex: 0.48,
  },
  cardNumberContainer: {
    position: 'relative',
  },
  cardIcon: {
    position: 'absolute',
    right: 12,
    top: '50%',
    marginTop: -10, // Half of icon height to center it
    zIndex: 1,
  },
  acceptedCardsContainer: {
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.2)',
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  acceptedCardsRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
  },
  feeNotice: {
    fontSize: 14,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_SECONDARY,
    lineHeight: 20,
  },
});

export default CreditCardDetailsForm;
